import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { HHMM } from "@rrsolutionsit/atl-utils";
import { Flight } from "../../../../models/flight.model";
import { FlightEditLookups } from "../../../form/flight-edit-lookups";
import { logTimeOf } from "../../../../flights2.pipes";
import { timeOfDay } from "../../../form/controls";
import { dateOf } from "../../../form/names";

/** The days and times of the flight as the form holds them. */
export interface Moments {
    startDate?: Date | null;
    flightDate?: Date | null;
    arrivalDate?: Date | null;
    stopDate?: Date | null;
    startTime?: string | null;
    takeOffTime?: string | null;
    landingTime?: string | null;
    stopTime?: string | null;
}

/** Each moment of the flight as the logbook keeps it: a day and a time of day. A ground run never takes off or lands: those controls are off. */
export function timesControls(flight: Flight, lookups: FlightEditLookups) {
    const off = <T>(value: T) => ({ value: value, disabled: lookups.isGroundRun(flight) });
    return {
        startDate: new FormControl<Date | null>(dateOf(flight.startDate), Validators.required),
        flightDate: new FormControl<Date | null>(off(dateOf(flight.flightDate)), Validators.required),
        arrivalDate: new FormControl<Date | null>(off(dateOf(flight.arrivalDate)), Validators.required),
        stopDate: new FormControl<Date | null>(dateOf(flight.stopDate), Validators.required),
        startTime: new FormControl<string | null>(logTimeOf(flight.startTime) || null, [Validators.required, timeOfDay]),
        takeOffTime: new FormControl<string | null>(off(logTimeOf(flight.takeOffTime) || null), [Validators.required, timeOfDay]),
        landingTime: new FormControl<string | null>(off(logTimeOf(flight.landingTime) || null), [Validators.required, timeOfDay]),
        stopTime: new FormControl<string | null>(logTimeOf(flight.stopTime) || null, [Validators.required, timeOfDay]),
    };
}

/** The minutes between the moments the form holds; a moment without its day takes the take off day. */
export function minutesOf(moments: Moments) {
    const at = (day: Date | null | undefined, value: string | null | undefined) => {
        if (!day || !value || timeOfDay({ value: value } as AbstractControl)) return null;
        const [hour, minute] = value.split(":").map(Number);
        return Date.UTC(day.getFullYear(), day.getMonth(), day.getDate(), hour, minute) / 60000;
    };
    const between = (from: number | null, to: number | null) => (from === null || to === null ? null : to - from);
    const takeOff = moments.flightDate;
    return {
        flightTime: between(at(takeOff, moments.takeOffTime), at(moments.arrivalDate ?? takeOff, moments.landingTime)),
        startStopTime: between(at(moments.startDate ?? takeOff, moments.startTime), at(moments.stopDate ?? moments.arrivalDate ?? takeOff, moments.stopTime)),
    };
}

/** Flight time and engine time as `HH:MM`, null while a day or a time is missing. */
export function durationsOf(moments: Moments): { flightTime: string | null; startStopTime: string | null } {
    const { flightTime, startStopTime } = minutesOf(moments);
    const text = (minutes: number | null) => (minutes === null ? null : new HHMM(`${minutes < 0 ? "-" : ""}${Math.floor(Math.abs(minutes) / 60)}:${Math.abs(minutes) % 60}`).value);
    return { flightTime: text(flightTime), startStopTime: text(startStopTime) };
}

/** Flight time and engine time follow from the dates and the times: neither may run backwards. */
export const forwardTimes: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
    // The raw value: the take off and landing of a ground run are off, and an off control leaves the plain value.
    const { flightTime, startStopTime } = minutesOf((group as FormGroup).getRawValue());
    const errors = {
        ...(flightTime !== null && flightTime < 0 ? { flightTime: true } : {}),
        ...(startStopTime !== null && startStopTime < 0 ? { startStopTime: true } : {}),
    };
    return Object.keys(errors).length ? errors : null;
};
